import {BreadcrumbsComponent} from "../../components/UI/breadcrumb/BreadcrumbComponent";
import React, {useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense} from "react";
import {Box, Container, maxWidth} from "@mui/system";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextareaAutosize, Tooltip, Typography} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import CustomerCompositionController from "../../API/customer/CustomerCompositionController";
import {authorization, buildCustomerRegistrationUrl, constantsErrorMessages} from "../../Constants";
import {saveAuthReturnPath} from "../../utils/authReturnPath";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import {ButtonGroupTabBar} from "../../components/UI/button/ButtonGroupTabBar";
import CheckIcon from "@mui/icons-material/Check";
import ModalAlert from "../../components/UI/table/customer-detales/ModalAlert";
import {EditLocalStoragePagesData} from "../../utils/EditLocalStoragePagesData";
import ButtonAdd from "../../components/UI/button/ButtonAdd";
import ButtonCancel from "../../components/UI/button/ButtonCancel";
import ButtonExit from "../../components/UI/button/ButtonExit";
import ButtonText from "../../components/UI/button/ButtonText";
import {
    getCompositionImageUrl,
    getCompositionThumbnailUrl,
    getSortedCompositionPictures
} from "../../utils/getCompositionPictures";
import {
    CatalogWidthRecalculatingOverlay,
    CompositionMediaImageMarkers,
    CompositionMediaModulePanel,
    useCompositionMediaHover,
    useCompositionMediaMarkers,
} from "../../components/composition/CompositionMediaView";
import {CompositionThumbnailsCarousel} from "../../components/composition/CompositionThumbnailsCarousel";
import OrderSpecificationModulesTable from "../../components/composition/OrderSpecificationModulesTable";
import {CompositionModuleChangeConfirmDialogContent} from "../../components/composition/CompositionModuleChangeConfirmDialogContent";
import {
    compositionMediaInfoCardMeasuredSx,
    compositionMediaModulePanelInCardSx,
} from "../../components/composition/compositionMediaLayoutSx";
import {
    buildCatalogDimensionBlocks,
    getCatalogDimensionCardCompactParts,
    buildCatalogModuleWidthPatchRequest,
    CATALOG_CURRENT_PRICE_TOOLTIP,
    CATALOG_RESET_BUTTON_TOOLTIP,
    CATALOG_RESET_UNAVAILABLE_TOOLTIP,
    CATALOG_WIDTH_RECALCULATING_INPUT_TOOLTIP,
    CATALOG_WIDTH_RECALCULATING_BUTTON_TOOLTIP,
    catalogItemGalleryStackMediaQuery,
    catalogItemGalleryStackMq,
    catalogItemMediaStackMediaQuery,
    catalogItemMediaStackMq,
    extractCatalogCompositionFieldsFromPatchResponse,
    extractCatalogCompositionDraftIdFromPatchResponse,
    extractCatalogPriceFromPatchResponse,
    extractModuleCompositionUpdatesFromPatchResponse,
    getModuleRowsFromCompositionPayload,
    mergeCatalogModuleCompositionUpdates,
    mergeCatalogTemplateModules,
    resolveCatalogItemCompositionFromGet,
    resolveCatalogCompositionOrderId,
    buildCreateOrderFromCompositionParams,
    resolveCustomerOrderId,
    markCustomerOrderCreatedFlash,
    isCatalogCompositionChanged,
    shouldShowCatalogCurrentPrice,
    unwrapCatalogPatchResponse,
    getDependentModuleCompositionsByParentId,
    getCatalogCharacteristicDimensionRows,
    isCatalogGuestUser,
    formatCompositionShortTopBottom,
    formatCatalogPriceRub,
    getModuleCompositionPanelDimensionLabelAccusative,
    isCustomerModuleWidthLocked,
    applyModuleCompositionSizeOverlay,
    getModuleCompositionDimension,
    revertModuleCompositionRowWidth,
} from "../../utils/compositionMediaUtils";
import RichTextHtml from "../../components/UI/RichTextHtml";
import {isEmptyRichText} from "../../utils/richTextUtils";
import {isKuhniVersoMebelDomain, kuhniVersoTabletContainerQuerySx} from "../../utils/isKuhniVersoMebelDomain";
import {pickCompositionTextureIdsFromComposition} from "../../utils/compositionTextureAssignment";
import CatalogModulesViewModeToggle, {
    CATALOG_MODULES_VIEW_2D,
    CATALOG_MODULES_VIEW_3D,
} from "../../components/composition/CatalogModulesViewModeToggle";

const CustomerComposition3DCatalogPreview = lazy(() =>
    import("./settings/Composition3DView").then((module) => ({
        default: module.CustomerComposition3DCatalogPreview,
    })),
);

const areCatalogWidthsEqual = (left, right) => (left ?? null) === (right ?? null)

const catalogItemNarrowMq = "@media (max-width: 1023px)"
const catalogDimensionCardCompactMq = "@media (max-width: 600px)"
const catalogInfoActionsCompactMq = "@media (max-width: 460px)"
const catalogTab1FieldsCompactMq = "@media (max-width: 700px)"

const CATALOG_ITEM_IMAGE_MAX_WIDTH = 730
const CATALOG_ITEM_IMAGE_MAX_HEIGHT = 716
const CATALOG_ITEM_INFO_MIN_WIDTH = 670
const CATALOG_ITEM_INFO_MIN_WIDTH_COMPACT = 580
const CATALOG_ITEM_IMAGE_ASPECT_RATIO = "1 / 1"
const CATALOG_ITEM_CONTENT_MAX_WIDTH = 682
const CATALOG_GALLERY_GAP = 24
const CATALOG_INFO_COLUMN_GAP_COMPACT = 16
const CATALOG_INFO_COLUMN_GAP_STACKED = 12
const CATALOG_GALLERY_MARGIN_BOTTOM = 40
const CATALOG_INFO_ACTIONS_BLOCK_HEIGHT = 42
const catalogPageTitleSx = {
    fontSize: "24px",
    fontWeight: 700,
    [catalogItemMediaStackMq]: {
        fontSize: "20px",
    },
}

const catalogCompactHorizontalScrollSx = {
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
}

const catalogTabBarWrapSx = {
    "& .MuiButtonGroup-root .MuiButton-root": {
        fontSize: "16px",
        lineHeight: "20px",
    },
    [catalogItemMediaStackMq]: {
        flex: "1 1 100%",
        width: "100%",
        maxWidth: "100%",
        ...catalogCompactHorizontalScrollSx,
        "& .MuiButtonGroup-root": {
            height: "40px",
            width: "max-content",
        },
        "& .MuiButtonGroup-root .MuiButton-root": {
            fontSize: "16px",
            lineHeight: "20px",
            padding: "6px 8px",
            gap: "4px",
        },
        "& .MuiButtonGroup-root .MuiButton-root[tabindex] .MuiSvgIcon-root": {
            width: "14px",
            height: "14px",
        },
    },
    [catalogDimensionCardCompactMq]: {
        "& .MuiButtonGroup-root": {
            height: "36px",
        },
        "& .MuiButtonGroup-root .MuiButton-root": {
            padding: "4px 8px",
        },
    },
}

const catalogHeaderRowSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    alignItems: "center",
    width: "100%",
    paddingBottom: "10px",
    [catalogItemNarrowMq]: {
        position: "sticky",
        top: "27px",
        zIndex: (theme) => theme.zIndex.appBar - 1,
        backgroundColor: (theme) => theme.palette.background.paper,
    },
    [catalogDimensionCardCompactMq]: {
        top: "19px",
    },
}

const catalogHeaderGalleryStackSx = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
}

const catalogHeaderActionsSx = {
    display: "flex",
    flexShrink: 0,
    marginLeft: "auto",
    [catalogItemMediaStackMq]: {
        flex: "1 1 100%",
        width: "100%",
        maxWidth: "100%",
        marginLeft: 0,
        display: "block",
        direction: "rtl",
        ...catalogCompactHorizontalScrollSx,
        [catalogDimensionCardCompactMq]: {
            display: "flex",
            direction: "ltr",
            overflowX: "visible",
            overflowY: "visible",
        },
    },
}

const catalogHeaderActionsInnerSx = {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexShrink: 0,
    width: "max-content",
    direction: "ltr",
    [catalogDimensionCardCompactMq]: {
        width: "100%",
        maxWidth: "100%",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        boxSizing: "border-box",
    },
}

const catalogCompactButtonAddSx = {
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        height: "36px",
        minHeight: "36px",
        padding: "6px 14px",
    },
}

const catalogHeaderOrderButtonWrapSx = {
    display: "flex",
    flexShrink: 0,
    [catalogItemNarrowMq]: {
        display: "none",
    },
}

const CATALOG_FIXED_ORDER_BAR_HEIGHT = 82

const catalogFixedOrderBarSx = {
    display: "none",
    [catalogItemNarrowMq]: {
        display: "flex",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        justifyContent: "center",
        alignItems: "center",
        px: "16px",
        pt: "8px",
        paddingBottom: "calc(40px + env(safe-area-inset-bottom, 0px))",
    },
}

const catalogFixedOrderButtonSx = {
    width: "100%",
    maxWidth: "293px",
    minWidth: "unset",
    minHeight: "32px",
    height: "auto",
    padding: "6px 8px 4px 8px",
    boxSizing: "border-box",
    fontSize: "16px",
    lineHeight: 1,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.14)",
    "&:hover": {
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
    },
}

const catalogAuthDialogButtonAddSx = {
    width: "auto",
    minWidth: "auto",
    height: "auto",
    minHeight: "unset",
    padding: "16px 32px",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        width: "auto",
        minWidth: "auto",
        height: "auto",
        minHeight: "unset",
        padding: "10px 20px",
    },
}

const catalogAuthDialogButtonExitSx = {
    width: "auto",
    minWidth: "auto",
    height: "auto",
    minHeight: "unset",
    padding: "16px 24px",
    [catalogItemMediaStackMq]: {
        padding: "10px 18px",
    },
}

const catalogAuthDialogActionsMq360 = "@media (max-width: 360px)";

const catalogAuthDialogActionsSx = {
    p: "1.25rem",
    justifyContent: "center",
    gap: "12px",
    flexWrap: "wrap",
    [catalogAuthDialogActionsMq360]: {
        p: "12px",
    },
}

const catalogCompactButtonCancelOrderSx = {
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        height: "36px",
        minHeight: "36px",
        width: "108px",
        minWidth: "108px",
        padding: "6px 12px",
    },
}

const catalogCompactButtonCancelWideSx = {
    [catalogItemNarrowMq]: {
        maxWidth: "402px",
    },
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        height: "36px",
        padding: "6px 12px",
    },
    [catalogItemGalleryStackMq]: {
        height: "34px",
        padding: "4px 8px",
    },
}

const catalogCompactButtonTextSx = {
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        padding: 0,
    },
}

const catalogDimensionCardsRowSx = {
    display: "flex",
    gap: "2px",
    flexShrink: 0,
    [catalogDimensionCardCompactMq]: {
        flex: "1 1 auto",
        width: "auto",
        maxWidth: "100%",
        minWidth: 0,
        alignItems: "stretch",
        boxSizing: "border-box",
    },
}

const catalogDimensionBoxSx = {
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    whiteSpace: "nowrap",
    bgcolor: (theme) => theme.palette.primary.lightGrey,
    border: (theme) => `1px solid ${theme.palette.primary.main}`,
    borderRadius: "8px",
    [catalogItemMediaStackMq]: {
        padding: "6px 8px",
        gap: "6px",
    },
    [catalogDimensionCardCompactMq]: {
        flex: "1 1 auto",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        gap: 0,
        padding: "4px 8px",
        whiteSpace: "normal",
        alignSelf: "stretch",
        overflowWrap: "anywhere",
    },
}

const catalogDimensionTypographySx = {
    fontSize: "16px",
    whiteSpace: "nowrap",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogDimensionCardCompactMq]: {
        display: "none",
    },
}

const catalogDimensionCompactTextSx = {
    display: "none",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.3,
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogDimensionCardCompactMq]: {
        display: "block",
    },
}

const catalogDimensionCompactLabelSx = {
    fontWeight: 600,
}

const catalogDimensionCompactValueGroupSx = {
    whiteSpace: "nowrap",
    display: "inline",
}

const catalogDimensionCompactUnitSx = {
    fontWeight: 600,
}

const CatalogDimensionCard = ({label, value}) => {
    const {name, displayValue} = getCatalogDimensionCardCompactParts(label, value)

    return (
        <Box sx={catalogDimensionBoxSx}>
            <Typography sx={{...catalogDimensionTypographySx, fontWeight: 600}}>
                {label}
            </Typography>
            <Typography sx={catalogDimensionTypographySx}>
                {value}
            </Typography>
            <Typography sx={catalogDimensionCompactTextSx} component="div">
                <Box component="span" sx={catalogDimensionCompactLabelSx}>
                    {name}:
                </Box>{" "}
                <Box component="span" sx={catalogDimensionCompactValueGroupSx}>
                    <Box component="span">{displayValue}</Box>
                    <Box component="span" sx={catalogDimensionCompactUnitSx}>
                        {"\u00A0"}мм
                    </Box>
                </Box>
            </Typography>
        </Box>
    )
}

const catalogContentSectionTitleSx = {
    fontSize: "17px",
    fontWeight: 600,
    [catalogItemMediaStackMq]: {
        fontSize: "15px",
    },
}

const catalogContentRowLabelSx = {
    fontSize: "16px",
    fontWeight: 600,
    minWidth: 0,
    width: "100%",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        fontSize: "16px",
    },
    [catalogTab1FieldsCompactMq]: {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
    },
}

const catalogContentRowValueSx = {
    fontSize: "16px",
    fontWeight: 400,
    minWidth: 0,
    width: "100%",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        fontSize: "16px",
    },
    [catalogTab1FieldsCompactMq]: {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
    },
}

const catalogTabContentSx = {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    [catalogItemMediaStackMq]: {
        gap: "12px",
        padding: "16px",
    },
    [catalogItemGalleryStackMq]: {
        gap: "8px",
        padding: "12px",
    },
}

const catalogTabContentSectionSx = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    [catalogItemMediaStackMq]: {
        gap: "8px",
    },
    [catalogItemGalleryStackMq]: {
        gap: "6px",
    },
}

const catalogContentFieldRowSx = {
    backgroundColor: "inherit",
    borderRadius: "8px",
    width: "100%",
    maxWidth: "100%",
    padding: "8px 12px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    minHeight: "40px",
    [catalogItemMediaStackMq]: {
        padding: "6px 8px",
        minHeight: "36px",
    },
    [catalogItemGalleryStackMq]: {
        borderRadius: "6px",
        padding: "4px 6px",
        minHeight: "32px",
        maxWidth: "100%",
    },
    [catalogItemNarrowMq]: {
        padding: "2px 4px",
    },
    [catalogTab1FieldsCompactMq]: {
        alignItems: "flex-start",
        minHeight: "auto",
    },
}

const catalogContentFieldRowFixedSx = {
    ...catalogContentFieldRowSx,
    height: "40px",
    [catalogItemMediaStackMq]: {
        height: "36px",
    },
    [catalogItemGalleryStackMq]: {
        height: "32px",
    },
    [catalogTab1FieldsCompactMq]: {
        height: "auto",
        minHeight: "40px",
    },
}

const catalogContentFieldLabelCellSx = {
    flex: "0 0 40%",
    width: "40%",
    maxWidth: "40%",
    minWidth: 0,
    flexShrink: 1,
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    overflow: "hidden",
    borderRight: "1px solid #EAEBEA",
    paddingRight: "12px",
    boxSizing: "border-box",
    [catalogItemMediaStackMq]: {
        flex: "0 0 40%",
        width: "40%",
        maxWidth: "40%",
        paddingRight: "8px",
    },
    [catalogItemGalleryStackMq]: {
        flex: "0 0 40%",
        width: "40%",
        maxWidth: "40%",
        paddingRight: "6px",
    },
    [catalogItemNarrowMq]: {
        paddingRight: "4px",
    },
    [catalogTab1FieldsCompactMq]: {
        flex: "0 0 50%",
        width: "50%",
        maxWidth: "50%",
        alignItems: "flex-start",
        overflow: "visible",
    },
}

const catalogContentFieldValueCellSx = {
    flex: "1 1 0",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    padding: "0 12px",
    boxSizing: "border-box",
    [catalogItemMediaStackMq]: {
        padding: "0 8px",
    },
    [catalogItemGalleryStackMq]: {
        padding: "0 6px",
    },
    [catalogItemNarrowMq]: {
        padding: "0 4px",
    },
    [catalogTab1FieldsCompactMq]: {
        flex: "0 0 50%",
        width: "50%",
        maxWidth: "50%",
        alignItems: "flex-start",
        overflow: "visible",
    },
}

const catalogGalleryRowSx = {
    display: "flex",
    flexWrap: "nowrap",
    gap: `${CATALOG_GALLERY_GAP}px`,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    alignItems: "flex-start",
    overflow: "hidden",
    marginBottom: `${CATALOG_GALLERY_MARGIN_BOTTOM}px`,
    [catalogItemGalleryStackMq]: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "auto",
        columnGap: "12px",
        alignItems: "start",
    },
    [catalogItemNarrowMq]: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        alignItems: "stretch",
    },
}

const catalogMediaStackSx = {
    display: "contents",
    [catalogItemGalleryStackMq]: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        gridColumn: 1,
        gridRow: 1,
        minWidth: 0,
        width: "100%",
    },
}

const catalogAdvantagesMediaHiddenNarrowSx = {
    [catalogItemNarrowMq]: {
        display: "none",
    },
}

const catalogInfoColumnSx = {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: CATALOG_ITEM_IMAGE_MAX_WIDTH,
    width: "100%",
    display: "flex",
    gap: `${CATALOG_GALLERY_GAP}px`,
    flexDirection: "column",
    [catalogItemMediaStackMq]: {
        gap: "16px",
    },
    [catalogItemGalleryStackMq]: {
        gridColumn: 2,
        gridRow: 1,
        alignSelf: "start",
        maxWidth: "none",
        minHeight: 0,
        gap: `${CATALOG_INFO_COLUMN_GAP_STACKED}px`,
    },
    [catalogItemNarrowMq]: {
        minWidth: "100%",
        width: "100%",
    },
}

const catalogThumbnailItemSx = {
    width: "112px",
    height: "112px",
    boxSizing: "border-box",
    flexShrink: 0,
    borderRadius: "16px",
    overflow: "hidden",
}

const catalogImageBoxSx = {
    position: "relative",
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: CATALOG_ITEM_IMAGE_MAX_WIDTH,
    aspectRatio: CATALOG_ITEM_IMAGE_ASPECT_RATIO,
    alignSelf: "flex-start",
    borderRadius: "16px",
    overflow: "hidden",
    boxSizing: "border-box",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    backgroundColor: (theme) => theme.palette.background.grey,
    [catalogItemGalleryStackMq]: {
        order: 1,
        flex: "0 0 auto",
        alignSelf: "stretch",
        width: "100%",
        maxWidth: "100%",
    },
}

const catalogMediaViewModeToggleWrapSx = {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 2,
}

const catalogInfoCardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    borderRadius: "16px",
    overflow: "hidden",
    flex: "0 0 auto",
}

const catalogInfoCardExpandedSx = {
    minHeight: "auto",
    flex: "0 0 auto",
    [catalogItemGalleryStackMq]: {
        minHeight: "auto",
        flex: "0 0 auto",
    },
}

const catalogTab1ContentSx = {
    ...catalogTabContentSx,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
}

const catalogTab1ContentExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
}

const catalogOrderDocumentTabWrapSx = {
    display: "flex",
    flexDirection: "column",
    gap: `${CATALOG_GALLERY_GAP}px`,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginBottom: `${CATALOG_GALLERY_MARGIN_BOTTOM}px`,
    [catalogItemMediaStackMq]: {
        gap: "16px",
    },
}

const catalogOrderDocumentTabCardSx = {
    ...catalogInfoCardSx,
    width: "100%",
    maxWidth: CATALOG_ITEM_IMAGE_MAX_WIDTH * 4 / 3,
    minHeight: "auto",
    height: "auto",
}

const catalogTab1ModificationsSectionSx = {
    ...catalogTabContentSectionSx,
    flexShrink: 0,
}

const catalogTab1CharacteristicsSectionSx = {
    ...catalogTabContentSectionSx,
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
}

const catalogTab1CharacteristicsSectionExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
}

const catalogTab1CharacteristicsRowsSx = {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
}

const catalogTab1CharacteristicsRowsExpandedSx = {
    flex: "0 0 auto",
}

const catalogTab1CharacteristicsFooterSx = {
    flexShrink: 0,
}

const catalogTabExpandableContentSx = {
    ...catalogTabContentSx,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
}

const catalogTabExpandableContentExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
}

const catalogTabExpandableBodySx = {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
}

const catalogTabExpandableBodyExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
    overflow: "visible",
}

const catalogInfoActionsSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    alignItems: "center",
    flexShrink: 0,
    marginBottom: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    [catalogItemMediaStackMq]: {
        gap: "12px",
    },
    "& > .MuiTooltip-root": {
        maxWidth: "100%",
    },
    [catalogInfoActionsCompactMq]: {
        "& > .MuiButton-root, & > .MuiTooltip-root, & > .MuiBox-root": {
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
        },
        "& > .MuiTooltip-root > span": {
            display: "flex",
            width: "100%",
            maxWidth: "100%",
        },
    },
}

const catalogInfoActionsButtonSx = {
    width: "auto",
    minWidth: "auto",
    maxWidth: "100%",
    boxSizing: "border-box",
    "@media (max-width: 1450px)": {
        width: "auto",
        minWidth: "auto",
    },
    [catalogInfoActionsCompactMq]: {
        width: "100%",
        minWidth: "unset",
    },
}

const recalculatePriceButtonShimmerSx = {
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "42%",
        height: "100%",
        borderRadius: "inherit",
        background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.38) 48%, transparent 100%)",
        transform: "translateX(-160%) skewX(-18deg)",
        animation: "recalculatePriceShimmer 4s ease-in-out infinite",
        WebkitAnimation: "recalculatePriceShimmer 4s ease-in-out infinite",
        pointerEvents: "none",
        zIndex: 1,
    },
    "@keyframes recalculatePriceShimmer": {
        "0%": {
            transform: "translateX(-160%) skewX(-18deg)",
            opacity: 0,
        },
        "8%": {
            opacity: 1,
        },
        "38%": {
            transform: "translateX(280%) skewX(-18deg)",
            opacity: 1,
        },
        "45%": {
            opacity: 0,
        },
        "100%": {
            transform: "translateX(280%) skewX(-18deg)",
            opacity: 0,
        },
    },
    "&.Mui-disabled::after": {
        animation: "none",
        WebkitAnimation: "none",
        opacity: 0,
    },
}

const catalogInfoActionsCancelButtonSx = {
    ...catalogInfoActionsButtonSx,
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        height: "36px",
        minHeight: "36px",
        padding: "6px 12px",
    },
}

const catalogItemPriceBlockSx = {
    boxSizing: "border-box",
    flexShrink: 0,
    minWidth: "252px",
    width: "auto",
    maxWidth: "100%",
    height: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "0 12px",
    borderRadius: "100px",
    border: (theme) => `${theme.palette.secondary.add} 1px solid`,
    backgroundColor: "transparent",
    overflow: "hidden",
    [catalogItemMediaStackMq]: {
        height: "36px",
        gap: "4px",
        padding: "0 10px",
    },
    [catalogInfoActionsCompactMq]: {
        width: "100%",
        minWidth: "unset",
    },
}

const catalogItemPriceLabelSx = {
    color: (theme) => theme.palette.text.main,
    whiteSpace: "nowrap",
    flexShrink: 0,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.2,
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
}

const catalogItemPriceTextSx = {
    color: (theme) => theme.palette.text.main,
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.2,
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
}

const CatalogItem = () => {
    const isKuhniVersoMebel = isKuhniVersoMebelDomain();
    const id = useParams().id;
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [alertData, setAlertData] = useState({});
    const [isLoading, setIsLoading] = useState(true)
    const [optionOpen, setOptionOpen] = useState(false)
    const [catalogCardExpanded, setCatalogCardExpanded] = useState(false)
    const [catalogTabContentOverflows, setCatalogTabContentOverflows] = useState(false)
    const catalogTabContentRef = useRef(null)
    const [colorOpen, setColorOpen] = useState(false)
    const [tab, setTab] = useState(1);
    const [selectedPictureId, setSelectedPictureId] = useState(null);
    const [moduleCompositions, setModuleCompositions] = useState([]);
    const pathname = useLocation().pathname
    const mainImageRef = useRef(null)
    const catalogImageBoxRef = useRef(null)
    const catalogMediaStackRef = useRef(null)
    const catalogInfoActionsRef = useRef(null)
    const isGalleryStacked = useMediaQuery(catalogItemGalleryStackMediaQuery)
    const isInfoColumnGapCompact = useMediaQuery(catalogItemMediaStackMediaQuery)
    const isTab1FieldsCompact = useMediaQuery("(max-width: 700px)")
    const isCatalogItemNarrow = useMediaQuery("(max-width: 1023px)")
    const isSpecificationTab = isKuhniVersoMebel && tab === 4
    const [catalogModulesViewMode, setCatalogModulesViewMode] = useState(CATALOG_MODULES_VIEW_2D)
    const isCatalogTab2Modules3d = tab === 2 && catalogModulesViewMode === CATALOG_MODULES_VIEW_3D
    const isFullWidthCatalogTab = isSpecificationTab
    const hideAdvantagesGalleryMedia = tab === 3 && isCatalogItemNarrow
    const [catalogImageBoxHeight, setCatalogImageBoxHeight] = useState(null)
    const [catalogCardBodyHeight, setCatalogCardBodyHeight] = useState(null)
    const [catalogMediaStackHeight, setCatalogMediaStackHeight] = useState(null)
    const pictures = getSortedCompositionPictures(data);
    const firstPicture = pictures[0] ?? null
    const secondPicture = pictures[1] ?? null
    const cabinetsTabPicture = secondPicture ?? firstPicture
    const selectedPicture = pictures.find((picture) => picture.id === selectedPictureId) ?? firstPicture;
    const galleryPicture = tab === 2 ? cabinetsTabPicture : selectedPicture;
    const isFirstPictureSelected =
        tab === 2
            ? Boolean(galleryPicture)
            : firstPicture != null && selectedPicture?.id === firstPicture.id
    const mainImageUrl = getCompositionImageUrl(data, galleryPicture);
    const {
        hoveredModuleRowId,
        moduleRowHoverPinned,
        setModuleRowHover,
        clearModuleRowHover,
        pinModuleRowHover,
        unpinModuleRowHover,
    } = useCompositionMediaHover()
    const {markers, handleMainImageLoad} = useCompositionMediaMarkers({
        moduleCompositions,
        enabled: tab === 2 && catalogModulesViewMode === CATALOG_MODULES_VIEW_2D,
        isFirstPictureSelected,
        mainImageUrl,
        mainImageRef,
    })

    const updateCatalogImageLayoutHeights = useCallback(() => {
        const infoColumnGap = isGalleryStacked
            ? CATALOG_INFO_COLUMN_GAP_STACKED
            : isInfoColumnGapCompact
                ? CATALOG_INFO_COLUMN_GAP_COMPACT
                : CATALOG_GALLERY_GAP
        const actionsHeight = catalogInfoActionsRef.current
            ? Math.round(catalogInfoActionsRef.current.getBoundingClientRect().height)
            : CATALOG_INFO_ACTIONS_BLOCK_HEIGHT
        if (isGalleryStacked) {
            const mediaStackEl = catalogMediaStackRef.current
            if (!mediaStackEl) {
                setCatalogImageBoxHeight(null)
                setCatalogCardBodyHeight(null)
                setCatalogMediaStackHeight(null)
                return
            }
            const stackHeight = Math.round(mediaStackEl.getBoundingClientRect().height)
            if (stackHeight <= 0) {
                return
            }
            setCatalogImageBoxHeight(null)
            setCatalogMediaStackHeight(stackHeight)
            setCatalogCardBodyHeight(
                stackHeight - infoColumnGap - actionsHeight,
            )
            return
        }
        const imageBoxEl = catalogImageBoxRef.current
        if (!imageBoxEl) {
            setCatalogImageBoxHeight(null)
            setCatalogCardBodyHeight(null)
            setCatalogMediaStackHeight(null)
            return
        }
        const imageHeight = imageBoxEl.getBoundingClientRect().height
        if (imageHeight <= 0) {
            return
        }
        const roundedHeight = Math.round(imageHeight)
        setCatalogImageBoxHeight(roundedHeight)
        setCatalogMediaStackHeight(roundedHeight)
        setCatalogCardBodyHeight(
            roundedHeight - infoColumnGap - actionsHeight,
        )
    }, [isGalleryStacked, isInfoColumnGapCompact])

    useEffect(() => {
        if (isLoading || isFullWidthCatalogTab) {
            return undefined
        }
        let resizeObserver = null
        let rafId = null

        const connectLayoutObserver = () => {
            const layoutEl = isGalleryStacked
                ? catalogMediaStackRef.current
                : catalogImageBoxRef.current
            if (!layoutEl) {
                return false
            }
            updateCatalogImageLayoutHeights()
            resizeObserver = new ResizeObserver(updateCatalogImageLayoutHeights)
            resizeObserver.observe(layoutEl)
            if (isGalleryStacked && catalogImageBoxRef.current) {
                resizeObserver.observe(catalogImageBoxRef.current)
            }
            if (catalogInfoActionsRef.current) {
                resizeObserver.observe(catalogInfoActionsRef.current)
            }
            return true
        }

        if (!connectLayoutObserver()) {
            rafId = requestAnimationFrame(() => {
                connectLayoutObserver()
            })
        }

        return () => {
            if (rafId != null) {
                cancelAnimationFrame(rafId)
            }
            resizeObserver?.disconnect()
        }
    }, [
        isLoading,
        isFullWidthCatalogTab,
        isGalleryStacked,
        isInfoColumnGapCompact,
        updateCatalogImageLayoutHeights,
        tab,
        catalogModulesViewMode,
        mainImageUrl,
        pictures.length,
    ])

    const isTab1CharacteristicsExpanded = tab === 1 && optionOpen
    const isCatalogCardExpanded =
        isTab1CharacteristicsExpanded ||
        ((tab === 2 || tab === 3) && catalogCardExpanded)
    const useMeasuredCatalogCardHeight =
        catalogCardBodyHeight != null && !isCatalogCardExpanded && !hideAdvantagesGalleryMedia
    const showCatalogExpandControl =
        (tab === 2 || tab === 3) &&
        (catalogTabContentOverflows || catalogCardExpanded)

    const checkCatalogTabContentOverflow = useCallback(() => {
        if (tab !== 2 && tab !== 3) {
            setCatalogTabContentOverflows(false)
            return
        }
        const el = catalogTabContentRef.current
        if (!el || catalogCardBodyHeight == null) {
            setCatalogTabContentOverflows(false)
            return
        }
        // На `tab 2` модульная панель (`CompositionMediaModulePanel`) скроллится внутри,
        // поэтому переполнение чаще “живёт” не на контейнере, а на первом дочернем элементе.
        const targetEl = tab === 2 ? el.firstElementChild : el
        const measuredEl = targetEl instanceof Element ? targetEl : el
        setCatalogTabContentOverflows(measuredEl.scrollHeight > measuredEl.clientHeight + 1)
    }, [tab, catalogCardBodyHeight])

    useEffect(() => {
        setCatalogCardExpanded(false)
    }, [tab])

    useEffect(() => {
        if (tab !== 2 && tab !== 3) {
            return undefined
        }
        const scheduleOverflowCheck = () => {
            requestAnimationFrame(checkCatalogTabContentOverflow)
        }
        scheduleOverflowCheck()
        const el = catalogTabContentRef.current
        if (!el) {
            return undefined
        }
        const resizeObserver = new ResizeObserver(scheduleOverflowCheck)
        resizeObserver.observe(el)
        if (el.firstElementChild instanceof Element) {
            resizeObserver.observe(el.firstElementChild)
        }
        return () => resizeObserver.disconnect()
    }, [
        tab,
        checkCatalogTabContentOverflow,
        catalogCardBodyHeight,
        catalogCardExpanded,
        moduleCompositions,
        data?.description,
        isLoading,
    ])

    const catalogCardMeasuredHeightSx = useMeasuredCatalogCardHeight
        ? {
            minHeight: `${catalogCardBodyHeight}px`,
            height: `${catalogCardBodyHeight}px`,
            maxHeight: `${catalogCardBodyHeight}px`,
            ...compositionMediaInfoCardMeasuredSx,
        }
        : null
    const catalogInfoColumnMeasuredSx = useMeasuredCatalogCardHeight && catalogMediaStackHeight != null
        ? {
            height: `${catalogMediaStackHeight}px`,
            minHeight: `${catalogMediaStackHeight}px`,
            maxHeight: `${catalogMediaStackHeight}px`,
        }
        : null
    const catalogMediaRowIdPrefix = "catalog-media-module-row-"
    const [catalogWidthConfirm, setCatalogWidthConfirm] = useState(null)
    const [catalogWidthRecalculating, setCatalogWidthRecalculating] = useState(false)
    const [isPriceActual, setIsPriceActual] = useState(false)
    const [priceRecalculating, setPriceRecalculating] = useState(false)
    const catalogWidthPendingRef = useRef(null)
    const priceActualBeforeWidthSubmitRef = useRef(undefined)
    const catalogWidthConfirmContext = useMemo(() => {
        if (catalogWidthConfirm?.rowId == null) {
            return null
        }
        const row = moduleCompositions.find((r) => r.id === catalogWidthConfirm.rowId)
        if (!row) {
            return null
        }
        const dependents = getDependentModuleCompositionsByParentId(
            row.id,
            moduleCompositions,
        )
        const formatWidth = (value) => (value == null || value === "" ? "—" : `${value} мм`)
        return {
            row,
            dependents,
            dimensionLabel: getModuleCompositionPanelDimensionLabelAccusative(),
            previousValue: formatWidth(catalogWidthConfirm.previous),
            nextValue: formatWidth(catalogWidthConfirm.next),
        }
    }, [catalogWidthConfirm, moduleCompositions])
    const catalogWidthEditStartRef = useRef(null)
    const catalogWidthBlurTimeoutRef = useRef(null)
    const catalogWidthSkipBlurCommitRef = useRef(false)

    const catalogModuleBaselineRef = useRef([])
    const catalogInitialSnapshotRef = useRef(null)
    const [catalogResetConfirmOpen, setCatalogResetConfirmOpen] = useState(false)
    const [catalogAuthDialogOpen, setCatalogAuthDialogOpen] = useState(false)
    const [orderConfirmOpen, setOrderConfirmOpen] = useState(false)
    const [compositionOriginalId, setCompositionOriginalId] = useState(null)
    const [compositionDraftId, setCompositionDraftId] = useState(null)
    const [isCreatingOrder, setIsCreatingOrder] = useState(false)
    const [catalogResetLoading, setCatalogResetLoading] = useState(false)
    const catalogAuthUrl =
        authorization + "/oauth2/authorization/" + window.location.host + "-authorization-code";
    const moduleCompositionsRef = useRef(moduleCompositions)
    moduleCompositionsRef.current = moduleCompositions
    const syncCatalogModuleBaseline = useCallback((modules) => {
        const list = Array.isArray(modules) ? modules : []
        catalogModuleBaselineRef.current = list.map((row) => ({...row}))
    }, [])

    const applyCatalogCompositionData = useCallback((
        compositionData,
        isInitialLoad = false,
        templateData = null,
    ) => {
        setData(compositionData)
        const modules = getModuleRowsFromCompositionPayload(compositionData)
        setModuleCompositions(modules)
        syncCatalogModuleBaseline(modules)
        if (isInitialLoad) {
            const baseline = templateData ?? compositionData
            const baselineModules = getModuleRowsFromCompositionPayload(baseline)
            catalogInitialSnapshotRef.current = {
                moduleCompositions: baselineModules.map((row) => ({...row})),
                price: baseline?.price ?? null,
                dimensions: extractCatalogCompositionFieldsFromPatchResponse(baseline),
            }
        }
    }, [syncCatalogModuleBaseline])

    const applyCatalogWidthPatchResponse = useCallback(
        (responseData) => {
            const payload = unwrapCatalogPatchResponse(responseData)
            if (payload == null) {
                return Promise.resolve()
            }

            const compositionPatch =
                extractCatalogCompositionFieldsFromPatchResponse(payload)
            const price = extractCatalogPriceFromPatchResponse(payload)
            const moduleRows = extractModuleCompositionUpdatesFromPatchResponse(responseData)

            const patchCatalogCompositionMeta = () => {
                setData((prev) => {
                    if (!prev) {
                        return prev
                    }
                    return {
                        ...prev,
                        ...compositionPatch,
                        ...(price != null ? {price} : {}),
                    }
                })
            }

            const isFullModuleList =
                moduleRows.length > 0 &&
                moduleRows.length >= moduleCompositionsRef.current.length

            if (isFullModuleList) {
                const templateModules =
                    catalogInitialSnapshotRef.current?.moduleCompositions
                    ?? moduleCompositionsRef.current
                const merged = mergeCatalogTemplateModules(templateModules, moduleRows)
                setModuleCompositions(merged)
                syncCatalogModuleBaseline(merged)
            } else if (moduleRows.length > 0) {
                setModuleCompositions((prev) => {
                    const merged = mergeCatalogModuleCompositionUpdates(prev, moduleRows)
                    syncCatalogModuleBaseline(merged)
                    return merged
                })
            }

            patchCatalogCompositionMeta()

            const draftIdFromPatch = extractCatalogCompositionDraftIdFromPatchResponse(responseData)
            if (draftIdFromPatch != null) {
                setCompositionDraftId(String(draftIdFromPatch))
            }

            return Promise.resolve()
        },
        [syncCatalogModuleBaseline],
    )

    const catalog3dWidthPostResponseRef = useRef(null)
    const [catalog3dWidthPostSeq, setCatalog3dWidthPostSeq] = useState(0)

    const notifyCatalog3dSceneWidthPost = useCallback((responseData) => {
        catalog3dWidthPostResponseRef.current = responseData
        setCatalog3dWidthPostSeq((seq) => seq + 1)
    }, [])

    const applyCatalogWidthPatchResponseWith3d = useCallback(
        (responseData) => {
            notifyCatalog3dSceneWidthPost(responseData)
            return applyCatalogWidthPatchResponse(responseData)
        },
        [applyCatalogWidthPatchResponse, notifyCatalog3dSceneWidthPost],
    )

    const handleModalAlert = (message, type = "error") => {
        setAlertData({
            type: type,
            open: true,
            message: type === "success" ? message : message.response?.data?.message || constantsErrorMessages[message.response?.status] || message.message
        })
    }

    const catalogHasChanges = useMemo(
        () => isCatalogCompositionChanged({
            moduleCompositions,
            compositionData: data,
            initialSnapshot: catalogInitialSnapshotRef.current,
            compositionDraftId,
        }),
        [moduleCompositions, data, compositionDraftId, isLoading],
    )
    const isCatalogResetDisabled = catalogWidthRecalculating || !catalogHasChanges
    const catalogResetTooltip = catalogHasChanges
        ? CATALOG_RESET_BUTTON_TOOLTIP
        : CATALOG_RESET_UNAVAILABLE_TOOLTIP

    const compositionTexturesForScene = useMemo(
        () => pickCompositionTextureIdsFromComposition(data),
        [data],
    )

    const reloadCatalogModuleCompositions = useCallback(() => {
        return CustomerCompositionController.getById(id, {catalogItemPage: true})
            .then(function (response) {
                const {template, draft} = response.data ?? {}
                const {composition, draftId} = resolveCatalogItemCompositionFromGet(template, draft)
                applyCatalogCompositionData(composition, false, template)
                setCompositionDraftId(draftId)
                setCompositionOriginalId(resolveCatalogCompositionOrderId(template, draft))
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
    }, [applyCatalogCompositionData, id])

    const handleCustomer3DWidthPatchResponse = useCallback((responseData) => {
        setIsPriceActual(false)
        return applyCatalogWidthPatchResponse(responseData)
    }, [applyCatalogWidthPatchResponse])

    const revertCatalogModuleWidth = useCallback((rowId, width) => {
        setModuleCompositions((prev) =>
            prev.map((row) =>
                row.id === rowId ? revertModuleCompositionRowWidth(row, width) : row,
            ),
        )
    }, [])

    const handleCatalogWidthConfirmCancel = useCallback(() => {
        setCatalogWidthConfirm((pending) => {
            if (pending) {
                revertCatalogModuleWidth(pending.rowId, pending.previous)
            }
            return null
        })
        catalogWidthEditStartRef.current = null
    }, [revertCatalogModuleWidth])

    const queueCatalogWidthConfirm = useCallback((rowId, previous, next) => {
        if (areCatalogWidthsEqual(previous, next)) {
            catalogWidthEditStartRef.current = null
            return
        }

        const row = moduleCompositionsRef.current.find((r) => r.id === rowId)
        if (row?.id == null) {
            revertCatalogModuleWidth(rowId, previous)
            catalogWidthEditStartRef.current = null
            return
        }
        if (isCustomerModuleWidthLocked(row)) {
            revertCatalogModuleWidth(rowId, previous)
            catalogWidthEditStartRef.current = null
            return
        }

        setCatalogWidthConfirm({
            rowId,
            previous,
            next,
        })
        catalogWidthEditStartRef.current = null
    }, [revertCatalogModuleWidth])

    const handleCatalogWidthConfirmSubmit = useCallback(() => {
        if (catalogWidthConfirm?.rowId == null) {
            handleCatalogWidthConfirmCancel()
            return
        }
        const {rowId, next, previous} = catalogWidthConfirm
        const compositionModule = moduleCompositionsRef.current.find((r) => r.id === rowId)
        if (!compositionModule) {
            handleCatalogWidthConfirmCancel()
            return
        }
        if (isCustomerModuleWidthLocked(compositionModule)) {
            handleCatalogWidthConfirmCancel()
            return
        }

        const patchRequest = buildCatalogModuleWidthPatchRequest({
            compositionModule,
            width: next,
            templateCompositionId: id,
            draftCompositionId: compositionDraftId,
        })
        if (patchRequest == null) {
            handleCatalogWidthConfirmCancel()
            return
        }

        catalogWidthPendingRef.current = {rowId, previous, next}
        priceActualBeforeWidthSubmitRef.current = isPriceActual
        setCatalogWidthConfirm(null)
        setIsPriceActual(false)
        setCatalogWidthRecalculating(true)

        CustomerCompositionController.module(patchRequest)
            .then(function (response) {
                return applyCatalogWidthPatchResponseWith3d(response.data)
            })
            .then(function () {
                setIsPriceActual(false)
                handleModalAlert("Данные изменены", "success")
            })
            .catch(function (error) {
                revertCatalogModuleWidth(rowId, previous)
                if (priceActualBeforeWidthSubmitRef.current !== undefined) {
                    setIsPriceActual(priceActualBeforeWidthSubmitRef.current)
                }
                priceActualBeforeWidthSubmitRef.current = undefined
                handleModalAlert(error)
            })
            .finally(function () {
                catalogWidthPendingRef.current = null
                setCatalogWidthRecalculating(false)
            })
    }, [
        catalogWidthConfirm,
        revertCatalogModuleWidth,
        handleCatalogWidthConfirmCancel,
        applyCatalogWidthPatchResponseWith3d,
        id,
        compositionDraftId,
        isPriceActual,
    ])

    const requestCatalogReset = useCallback(() => {
        if (catalogWidthRecalculating || !catalogHasChanges) {
            return
        }
        if (catalogWidthBlurTimeoutRef.current) {
            clearTimeout(catalogWidthBlurTimeoutRef.current)
            catalogWidthBlurTimeoutRef.current = null
        }
        if (catalogWidthConfirm) {
            handleCatalogWidthConfirmCancel()
        }
        catalogWidthEditStartRef.current = null
        setCatalogResetConfirmOpen(true)
    }, [catalogHasChanges, catalogWidthRecalculating, catalogWidthConfirm, handleCatalogWidthConfirmCancel])

    const performCatalogReset = useCallback(() => {
        const snapshot = catalogInitialSnapshotRef.current
        if (!snapshot) {
            setCatalogResetConfirmOpen(false)
            return
        }

        const applyLocalReset = () => {
            setCatalogWidthConfirm(null)
            catalogWidthEditStartRef.current = null
            const modules = snapshot.moduleCompositions.map((row) => ({...row}))
            setModuleCompositions(modules)
            syncCatalogModuleBaseline(modules)
            setData((prev) =>
                prev
                    ? {
                        ...prev,
                        ...(snapshot.dimensions ?? {}),
                        price: snapshot.price,
                    }
                    : prev,
            )
            setCompositionDraftId(null)
            setCompositionOriginalId(id)
            setIsPriceActual(true)
            setCatalogResetConfirmOpen(false)
        }

        if (compositionDraftId == null) {
            applyLocalReset()
            handleModalAlert("Сброс выполнен", "success")
            return
        }

        setCatalogResetLoading(true)
        CustomerCompositionController.deleteDraft(compositionDraftId)
            .then(function () {
                applyLocalReset()
                handleModalAlert("Сброс выполнен", "success")
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
            .finally(() => setCatalogResetLoading(false))
    }, [compositionDraftId, handleModalAlert, id, syncCatalogModuleBaseline])

    const handleCatalogResetConfirmDialogKeyDown = (event) => {
        if (event.key !== "Enter" || !catalogResetConfirmOpen) {
            return
        }
        if (event.target instanceof HTMLTextAreaElement) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        performCatalogReset()
    }

    const handleCloseAlert = () => {
        setAlertData((alert) => {
            return {...alert, open: false}
        })
    }
    const formatCurrentSizeValue = (value) =>
        value == null || value === "" ? "—" : `${value} мм`

    const characteristics = useMemo(() => [
        ...getCatalogCharacteristicDimensionRows(data),
        {
            text: "Текущие размеры",
            value:
                data?.cornerType === "STRAIGHT"
                    ? formatCurrentSizeValue(data?.lengthLeft)
                    : `${formatCurrentSizeValue(data?.lengthLeft)} / ${formatCurrentSizeValue(data?.lengthRight)}`,
        },
        {
            text: "Материал корпуса",
            value: optionOpen
                ? formatCompositionShortTopBottom(data?.nameBodyTop, data?.nameBodyBottom)
                : formatCompositionShortTopBottom(data?.nameShortBodyTop, data?.nameShortBodyBottom),
        },
        ...(optionOpen
            ? [{text: "Тип корпуса", value: formatCompositionShortTopBottom(data?.typeBodyTop, data?.typeBodyBottom)}]
            : []),
        {
            text: "Материал фасада",
            value: optionOpen
                ? formatCompositionShortTopBottom(data?.nameFrontTop, data?.nameFrontBottom)
                : formatCompositionShortTopBottom(data?.nameShortFrontTop, data?.nameShortFrontBottom),
        },
        ...(optionOpen
            ? [{text: "Тип фасада", value: formatCompositionShortTopBottom(data?.typeFrontTop, data?.typeFrontBottom)}]
            : []),
        ...(optionOpen ? [{text: "Тип верха столешницы", value: data?.typeTableTop}] : []),
        {text: "Материал верха столешницы", value: data?.nameShortTableTop},
        {text: "Обработка среза столешницы", value: data?.countertopCuttingTreatment},
        ...(optionOpen ? [{text: "Тип стеновой панели", value: data?.typeWallPanel}] : []),
        {text: "Материал стеновой панели", value: data?.nameShortWallPanel},
    ], [data, optionOpen])

    const handleCatalogModuleWidthChange = (rowId, rawValue) => {
        if (catalogWidthConfirm || catalogWidthRecalculating) {
            return
        }
        const row = moduleCompositionsRef.current.find((r) => r.id === rowId)
        if (row && isCustomerModuleWidthLocked(row)) {
            return
        }
        const num = rawValue === "" ? null : Number(rawValue)
        const value = rawValue === "" || Number.isNaN(num) ? null : num
        setModuleCompositions((prev) =>
            prev.map((row) =>
                row.id === rowId
                    ? applyModuleCompositionSizeOverlay(row, {width: value})
                    : row,
            ),
        )
    }

    const commitCatalogModuleWidthEdit = useCallback((rowId) => {
        if (catalogWidthConfirm || catalogWidthRecalculating) {
            return
        }
        const row = moduleCompositionsRef.current.find((r) => r.id === rowId)
        if (!row?.module?.id) {
            catalogWidthEditStartRef.current = null
            return
        }
        if (isCustomerModuleWidthLocked(row)) {
            catalogWidthEditStartRef.current = null
            return
        }

        const editStart = catalogWidthEditStartRef.current
        const previous = editStart?.rowId === rowId
            ? editStart.width
            : getModuleCompositionDimension(row, "width") ?? null
        const next = getModuleCompositionDimension(row, "width") ?? null

        queueCatalogWidthConfirm(rowId, previous, next)
    }, [catalogWidthConfirm, catalogWidthRecalculating, queueCatalogWidthConfirm])

    const handleCatalogModuleWidthFocus = (row) => {
        if (catalogWidthConfirm || catalogWidthRecalculating) {
            return
        }
        setModuleRowHover(row.id)
        if (catalogWidthBlurTimeoutRef.current) {
            clearTimeout(catalogWidthBlurTimeoutRef.current)
            catalogWidthBlurTimeoutRef.current = null
        }
        if (catalogWidthEditStartRef.current?.rowId !== row.id) {
            catalogWidthEditStartRef.current = {
                rowId: row.id,
                width: getModuleCompositionDimension(row, "width") ?? null,
            }
        }
    }

    const handleCatalogModuleWidthBlur = (rowId) => {
        if (catalogWidthConfirm || catalogWidthRecalculating) {
            return
        }
        if (catalogWidthSkipBlurCommitRef.current) {
            catalogWidthSkipBlurCommitRef.current = false
            return
        }
        if (catalogWidthBlurTimeoutRef.current) {
            clearTimeout(catalogWidthBlurTimeoutRef.current)
        }
        catalogWidthBlurTimeoutRef.current = window.setTimeout(() => {
            catalogWidthBlurTimeoutRef.current = null
            commitCatalogModuleWidthEdit(rowId)
        }, 0)
    }

    const handleCatalogModuleWidthKeyDown = (rowId, event) => {
        if (event.key !== "Enter") {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        if (catalogWidthBlurTimeoutRef.current) {
            clearTimeout(catalogWidthBlurTimeoutRef.current)
            catalogWidthBlurTimeoutRef.current = null
        }
        catalogWidthSkipBlurCommitRef.current = true
        commitCatalogModuleWidthEdit(rowId)
        event.currentTarget.blur()
    }

    const handleCatalogWidthConfirmDialogKeyDown = (event) => {
        if (event.key !== "Enter" || !catalogWidthConfirm) {
            return
        }
        if (event.target instanceof HTMLTextAreaElement) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        handleCatalogWidthConfirmSubmit()
    }

    useEffect(() => {
        let pagesData = {}
        try {
            pagesData = JSON.parse(localStorage.getItem('pagesData')) || {}
        } catch {
            pagesData = {}
        }
        const savedTab = pagesData[pathname]?.tab
        const allowedTabs = isKuhniVersoMebel ? [1, 2, 3, 4] : [1, 2, 3]
        if (savedTab === 5) {
            setTab(2)
            EditLocalStoragePagesData(pathname, "tab", 2)
        } else if (allowedTabs.includes(savedTab)) {
            setTab(savedTab)
        }
        const savedModulesViewMode = pagesData[pathname]?.modulesViewMode
        if (
            savedModulesViewMode === CATALOG_MODULES_VIEW_2D
            || savedModulesViewMode === CATALOG_MODULES_VIEW_3D
        ) {
            setCatalogModulesViewMode(savedModulesViewMode)
        }
        CustomerCompositionController.getById(id, {catalogItemPage: true}).then(function (response) {
            const {template, draft, actualPrice} = response.data ?? {};
            const {composition, draftId} = resolveCatalogItemCompositionFromGet(template, draft);
            applyCatalogCompositionData(composition, true, template);
            setCompositionDraftId(draftId);
            setCompositionOriginalId(resolveCatalogCompositionOrderId(template, draft));
            setIsPriceActual(shouldShowCatalogCurrentPrice({
                actualPrice,
                hasSecond: draft != null,
            }));
            const sortedPictures = getSortedCompositionPictures(composition);
            setSelectedPictureId(sortedPictures[0]?.id ?? null);
        })
            .catch(function (error) {
                handleModalAlert(error);
            }).finally(() => setIsLoading(false))
    }, [id, applyCatalogCompositionData, pathname])
    const handleChangeTab = (tab) => {
        setTab(tab)
        EditLocalStoragePagesData(pathname, "tab", tab)
    }

    const handleCatalogModulesViewModeChange = (nextMode) => {
        setCatalogModulesViewMode(nextMode)
        EditLocalStoragePagesData(pathname, "modulesViewMode", nextMode)
    }

    const redirectToAuth = () => {
        saveAuthReturnPath();
        window.location.href = catalogAuthUrl;
    };

    const redirectToRegistration = () => {
        saveAuthReturnPath();
        window.location.href = buildCustomerRegistrationUrl();
    };

    const handlePlaceOrderClick = () => {
        if (isCatalogGuestUser()) {
            setCatalogAuthDialogOpen(true);
            return;
        }
        if (isCreatingOrder || catalogWidthRecalculating) {
            return;
        }

        const compositionId = compositionOriginalId ?? data?.id;
        if (compositionId == null || compositionId === "") {
            handleModalAlert("Не удалось определить композицию для заказа");
            return;
        }

        setOrderConfirmOpen(true);
    };

    const handleCreateOrderCancel = () => {
        if (!isCreatingOrder) {
            setOrderConfirmOpen(false);
        }
    };

    const handleCreateOrderConfirm = () => {
        const orderParams = buildCreateOrderFromCompositionParams({
            originalCompositionId: compositionOriginalId ?? data?.id,
            draftId: compositionDraftId,
        });
        if (orderParams == null) {
            handleModalAlert("Не удалось определить композицию для заказа");
            return;
        }

        setIsCreatingOrder(true);
        CustomerCompositionController.createOrder(orderParams)
            .then(function (response) {
                const orderId = resolveCustomerOrderId(response.data);
                if (orderId == null) {
                    handleModalAlert("Не удалось определить заказ");
                    return Promise.reject();
                }
                return orderId;
            })
            .then(function (orderId) {
                if (orderId == null) {
                    return;
                }
                setOrderConfirmOpen(false);
                markCustomerOrderCreatedFlash();
                navigate(`/customer/orders/${orderId}`, {
                    state: {orderCreated: true},
                });
            })
            .catch(function (error) {
                if (error) {
                    handleModalAlert(error);
                }
            })
            .finally(() => setIsCreatingOrder(false));
    };

    const handleRecalculatePrice = () => {
        if (priceRecalculating || catalogWidthRecalculating) {
            return;
        }
        const compositionId = compositionDraftId ?? compositionOriginalId ?? data?.id ?? id;
        if (compositionId == null || compositionId === "") {
            handleModalAlert("Не удалось определить композицию для расчёта цены");
            return;
        }

        setPriceRecalculating(true);
        CustomerCompositionController.recalculatePrice(compositionId)
            .then(function (response) {
                const responseData = response?.data;
                if (responseData == null) {
                    return;
                }
                return applyCatalogWidthPatchResponse(responseData).then(function () {
                    const price = extractCatalogPriceFromPatchResponse(responseData);
                    const payload = unwrapCatalogPatchResponse(responseData);
                    if (price != null || payload?.actualPrice === true) {
                        setIsPriceActual(true);
                    }
                });
            })
            .catch(function (error) {
                handleModalAlert(error);
            })
            .finally(() => setPriceRecalculating(false));
    };

    const orderConfirmPriceText = formatCatalogPriceRub(data?.price, {from: false});
    const showCurrentPriceBlock = isPriceActual && data?.price != null;
    const showRecalculatePriceButton = !isPriceActual;

    return isLoading ?
        <Box sx={{
            height: `calc(100vh - 435px)`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
        }}>
            <CircularProgress disableShrink/>
        </Box> : <Paper sx={{
            width: '100%',
            paddingBottom: "40px",
            display: 'flex',
            justifyContent: "space-between",
            flexGrow: 1,
            scrollbarGutter: "stable",
            [catalogItemNarrowMq]: {
                paddingBottom: `calc(40px + ${CATALOG_FIXED_ORDER_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
            },
        }}>
            <Container sx={{
                "&.MuiContainer-root": {
                    maxWidth: "1668px",
                    display: 'flex',
                    flexDirection: "column",
                    gridGap: "100px",
                    ...(isKuhniVersoMebel ? kuhniVersoTabletContainerQuerySx : {}),
                },
            }}>
                <Box>
                    <BreadcrumbsComponent
                        page={data?.name}
                        path={[
                            {name: "Главная", href: "/"},
                            {name: "Каталог", href: "/catalog"},
                        ]}/>
                    <Box sx={{
                        // height: "1100px",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        gap: "32px",
                    }}>
                        <Typography sx={catalogPageTitleSx}>
                            Кухня {data?.name}
                        </Typography>
                        <Box sx={catalogHeaderGalleryStackSx}>
                            <Box sx={catalogHeaderRowSx}>
                                <Box sx={catalogTabBarWrapSx}>
                                <ButtonGroupTabBar activeTab={tab}>
                                    <Button tabIndex={1} onClick={() => handleChangeTab(1)}>
                                        <CheckIcon/>
                                        Основная информация
                                    </Button>
                                    <Button tabIndex={2} onClick={() => handleChangeTab(2)}>
                                        <CheckIcon/>
                                        Список шкафов
                                    </Button>
                                    {isKuhniVersoMebel && (
                                        <Button tabIndex={4} onClick={() => handleChangeTab(4)}>
                                            <CheckIcon/>
                                            Спецификация
                                        </Button>
                                    )}
                                    <Button tabIndex={3} onClick={() => handleChangeTab(3)}>
                                        <CheckIcon/>
                                        Преимущества
                                    </Button>
                                </ButtonGroupTabBar>
                                </Box>
                                <Box sx={catalogHeaderActionsSx}>
                                    <Box sx={catalogHeaderActionsInnerSx}>
                                    <Box sx={catalogDimensionCardsRowSx}>
                                        {buildCatalogDimensionBlocks(
                                            data?.cornerType,
                                            data?.lengthLeft,
                                            data?.lengthRight,
                                        ).map(({label, value}) => (
                                            <CatalogDimensionCard key={label} label={label} value={value}/>
                                        ))}
                                        {data?.height != null && (
                                            <CatalogDimensionCard label="Высота, мм" value={data.height}/>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        width: "44px",
                                        height: "44px",
                                        display: "none",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        border: (theme) => `1px solid ${theme.palette.secondary.add}`,
                                        borderRadius: "50%"
                                    }}>

                                    </Box>
                                    <Box sx={catalogHeaderOrderButtonWrapSx}>
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={catalogWidthRecalculating
                                                ? CATALOG_WIDTH_RECALCULATING_BUTTON_TOOLTIP
                                                : ""}
                                        >
                                            <Box
                                                component="span"
                                                sx={{display: "inline-flex", maxWidth: "100%"}}
                                            >
                                                <ButtonAdd
                                                    text={"Оформить заказ"}
                                                    width={164}
                                                    height={42}
                                                    sx={catalogCompactButtonAddSx}
                                                    loading={isCreatingOrder}
                                                    disabled={catalogWidthRecalculating}
                                                    onClick={handlePlaceOrderClick}
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {isSpecificationTab ? (
                                <Box sx={catalogOrderDocumentTabWrapSx}>
                                    <Box sx={catalogOrderDocumentTabCardSx}>
                                        <Box sx={catalogTabContentSx}>
                                            <Box sx={catalogTabContentSectionSx}>
                                                <Typography sx={catalogContentSectionTitleSx}>
                                                    Модули
                                                </Typography>
                                                <OrderSpecificationModulesTable
                                                    moduleCompositions={moduleCompositions}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                            <Box sx={catalogGalleryRowSx}>
                            <Box
                                ref={catalogMediaStackRef}
                                sx={{
                                    ...catalogMediaStackSx,
                                    ...(hideAdvantagesGalleryMedia
                                        ? catalogAdvantagesMediaHiddenNarrowSx
                                        : null),
                                }}
                            >
                            {tab !== 2 && (
                                <CompositionThumbnailsCarousel
                                    pictures={pictures}
                                    selectedPictureId={selectedPicture?.id}
                                    onSelectPicture={setSelectedPictureId}
                                    getThumbnailUrl={getCompositionThumbnailUrl}
                                    maxHeight={catalogImageBoxHeight ?? undefined}
                                    thumbnailItemSx={catalogThumbnailItemSx}
                                    thumbnailRailHeight={112}
                                />
                            )}
                            <Box ref={catalogImageBoxRef} sx={catalogImageBoxSx}>
                                {tab === 2 ? (
                                    <Box sx={catalogMediaViewModeToggleWrapSx}>
                                        <CatalogModulesViewModeToggle
                                            value={catalogModulesViewMode}
                                            onChange={handleCatalogModulesViewModeChange}
                                            disabled={isLoading}
                                        />
                                    </Box>
                                ) : null}
                                {tab === 2 && isCatalogTab2Modules3d ? (
                                    <Suspense
                                        fallback={(
                                            <Box sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}>
                                                <CircularProgress disableShrink/>
                                            </Box>
                                        )}
                                    >
                                        <CustomerComposition3DCatalogPreview
                                            compositionId={compositionOriginalId ?? id}
                                            customerComposition3dPathId={compositionOriginalId ?? id}
                                            customerComposition3dDraftId={compositionDraftId}
                                            customerComposition3dCatalogRequest
                                            moduleCompositions={moduleCompositions}
                                            hoveredModuleRowId={hoveredModuleRowId}
                                            setModuleRowHover={setModuleRowHover}
                                            clearModuleRowHover={clearModuleRowHover}
                                            moduleRowHoverPinned={moduleRowHoverPinned}
                                            pinModuleRowHover={pinModuleRowHover}
                                            unpinModuleRowHover={unpinModuleRowHover}
                                            moduleListRowIdPrefix={catalogMediaRowIdPrefix}
                                            customer3dWidthPostResponseRef={catalog3dWidthPostResponseRef}
                                            customer3dWidthPostSeq={catalog3dWidthPostSeq}
                                            compositionLengthLeft={data?.lengthLeft}
                                            compositionLengthRight={data?.lengthRight}
                                            compositionHeight={data?.height}
                                            compositionCornerType={data?.cornerType}
                                            compositionTextures={compositionTexturesForScene}
                                            handleModalAlert={handleModalAlert}
                                            previewContainerSx={{
                                                position: "absolute",
                                                inset: 0,
                                                minHeight: 0,
                                            }}
                                        />
                                    </Suspense>
                                ) : mainImageUrl ? (
                                    <Box
                                        component="img"
                                        ref={mainImageRef}
                                        src={mainImageUrl}
                                        alt=""
                                        onLoad={(event) => {
                                            updateCatalogImageLayoutHeights()
                                            if (tab === 2) {
                                                handleMainImageLoad(event)
                                            }
                                        }}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center",
                                            display: "block",
                                        }}
                                    />
                                ) : null}
                                {tab === 2 && !isCatalogTab2Modules3d ? (
                                    <CompositionMediaImageMarkers
                                        markers={markers}
                                        readOnly
                                        focusWidthOnClick
                                        rowIdPrefix={catalogMediaRowIdPrefix}
                                        hoveredModuleRowId={hoveredModuleRowId}
                                        moduleCompositions={moduleCompositions}
                                        setModuleRowHover={setModuleRowHover}
                                        clearModuleRowHover={clearModuleRowHover}
                                    />
                                ) : null}
                                {tab === 2 && isCatalogTab2Modules3d ? (
                                    <CatalogWidthRecalculatingOverlay
                                        active={catalogWidthRecalculating}
                                    />
                                ) : null}
                            </Box>
                            </Box>
                            <Box sx={{
                                ...catalogInfoColumnSx,
                                ...catalogInfoColumnMeasuredSx,
                                [catalogItemNarrowMq]: {
                                    height: "auto",
                                    minHeight: "auto",
                                    maxHeight: "none",
                                },
                            }}>
                                <Box
                                    sx={{
                                        ...catalogInfoCardSx,
                                        ...catalogCardMeasuredHeightSx,
                                        [catalogItemNarrowMq]: {
                                            height: "auto",
                                            minHeight: "auto",
                                            maxHeight: "none",
                                        },
                                        ...(isCatalogCardExpanded ? catalogInfoCardExpandedSx : null),
                                    }}
                                >
                                    {tab === 1 ? <Box sx={{
                                        ...catalogTab1ContentSx,
                                        ...(isTab1CharacteristicsExpanded ? catalogTab1ContentExpandedSx : null),
                                    }}>
                                        <Box sx={catalogTab1ModificationsSectionSx}>
                                            <Typography sx={catalogContentSectionTitleSx}>
                                                Модификации
                                            </Typography>
                                            <Box>
                                                {[{
                                                    text: "Раположение угла",
                                                    value: data?.cornerType === "CORNER_LEFT" ? "Левое" : "Правое"
                                                },
                                                    {text: "Коллекция", value: data?.collection},
                                                    {text: "Модель", value: data?.model},
                                                    {text: "Название расцветки", value: data?.colorScheme}
                                                ].map((el, i) => {
                                                    return ((data?.cornerType !== "STRAIGHT" && i === 0) || i === 1 || i === 2) &&
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                ...(optionOpen
                                                                    ? catalogContentFieldRowSx
                                                                    : catalogContentFieldRowFixedSx),
                                                                backgroundColor: i % 2 === 0 ? "#F5F6FA" : "white",
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                ...catalogContentFieldLabelCellSx,
                                                                ...(optionOpen && {
                                                                    overflow: "visible",
                                                                    alignItems: "flex-start",
                                                                    py: "4px",
                                                                }),
                                                            }}>
                                                                <Typography
                                                                    noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                    title={el.text}
                                                                    sx={catalogContentRowLabelSx}
                                                                >
                                                                    {el.text}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{
                                                                ...catalogContentFieldValueCellSx,
                                                                ...(optionOpen && {
                                                                    overflow: "visible",
                                                                    alignItems: "flex-start",
                                                                    py: "4px",
                                                                }),
                                                            }}>
                                                                <Typography
                                                                    noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                    title={el.value ?? ""}
                                                                    sx={catalogContentRowValueSx}
                                                                >
                                                                    {el.value}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                })}
                                            </Box>
                                            <ButtonCancel
                                                text={"Другие варианты расцветки"}
                                                onClick={() => {
                                                    const model = data?.model;
                                                    navigate(
                                                        model
                                                            ? `/catalog?model=${encodeURIComponent(model)}`
                                                            : "/catalog",
                                                    );
                                                }}
                                                width="auto"
                                                minWidth="auto"
                                                height={42}
                                                sx={catalogCompactButtonCancelWideSx}
                                            />
                                        </Box>
                                        <Box sx={{
                                            ...catalogTab1CharacteristicsSectionSx,
                                            ...(isTab1CharacteristicsExpanded
                                                ? catalogTab1CharacteristicsSectionExpandedSx
                                                : null),
                                        }}>
                                            <Typography sx={catalogContentSectionTitleSx}>
                                                Характеристика
                                            </Typography>
                                            <Box sx={{
                                                ...catalogTab1CharacteristicsRowsSx,
                                                ...(isTab1CharacteristicsExpanded
                                                    ? catalogTab1CharacteristicsRowsExpandedSx
                                                    : null),
                                            }}>
                                                {characteristics.map((el, i) => {
                                                    return (optionOpen || i < 8) && <Box
                                                        key={i}
                                                        sx={{
                                                            ...(optionOpen
                                                                ? catalogContentFieldRowSx
                                                                : catalogContentFieldRowFixedSx),
                                                            backgroundColor: i % 2 === 0 ? "#F5F6FA" : "white",
                                                        }}
                                                    >
                                                        <Box sx={{
                                                            ...catalogContentFieldLabelCellSx,
                                                            ...(optionOpen && {
                                                                overflow: "visible",
                                                                alignItems: "flex-start",
                                                                py: "4px",
                                                            }),
                                                        }}>
                                                            <Typography
                                                                noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                title={el.text}
                                                                sx={catalogContentRowLabelSx}
                                                            >
                                                                {el.text}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{
                                                            ...catalogContentFieldValueCellSx,
                                                            ...(optionOpen && {
                                                                overflow: "visible",
                                                                alignItems: "flex-start",
                                                                py: "4px",
                                                            }),
                                                        }}>
                                                            <Typography
                                                                noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                title={el.value ?? ""}
                                                                sx={catalogContentRowValueSx}
                                                            >
                                                                {el.value}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                })}
                                            </Box>
                                            <Box sx={catalogTab1CharacteristicsFooterSx}>
                                                <ButtonText
                                                    orange
                                                    onClick={() => setOptionOpen(!optionOpen)}
                                                    text={optionOpen ? "Скрыть характеристики" : "Открыть характеристики"}
                                                    sx={catalogCompactButtonTextSx}
                                                />
                                            </Box>
                                        </Box>
                                    </Box> : tab === 2 || tab === 3 ? (
                                        <Box
                                            sx={{
                                                ...catalogTabExpandableContentSx,
                                                ...(catalogCardExpanded
                                                    ? catalogTabExpandableContentExpandedSx
                                                    : null),
                                            }}
                                        >
                                            <Box
                                                ref={catalogTabContentRef}
                                                sx={{
                                                    ...catalogTabExpandableBodySx,
                                                    ...(catalogCardExpanded
                                                        ? catalogTabExpandableBodyExpandedSx
                                                        : null),
                                                }}
                                            >
                                                {tab === 2 ? (
                                                    <CompositionMediaModulePanel
                                                        moduleCompositions={moduleCompositions}
                                                        cornerType={data?.cornerType}
                                                        rowIdPrefix={catalogMediaRowIdPrefix}
                                                        hoveredModuleRowId={hoveredModuleRowId}
                                                        setModuleRowHover={setModuleRowHover}
                                                        clearModuleRowHover={clearModuleRowHover}
                                                        editableWidth
                                                        lockWidthByBlockSize
                                                        onWidthChange={handleCatalogModuleWidthChange}
                                                        onWidthFocus={handleCatalogModuleWidthFocus}
                                                        onWidthBlur={handleCatalogModuleWidthBlur}
                                                        onWidthKeyDown={handleCatalogModuleWidthKeyDown}
                                                        widthInputsDisabled={
                                                            Boolean(catalogWidthConfirm) ||
                                                            catalogWidthRecalculating
                                                        }
                                                        widthInputTooltip={
                                                            catalogWidthRecalculating
                                                                ? CATALOG_WIDTH_RECALCULATING_INPUT_TOOLTIP
                                                                : undefined
                                                        }
                                                        sx={{
                                                            ...compositionMediaModulePanelInCardSx,
                                                            overflowY: catalogCardExpanded
                                                                ? "auto"
                                                                : "hidden",
                                                        }}
                                                    />
                                                ) : isEmptyRichText(data?.description) ? (
                                                    <Typography
                                                        sx={{
                                                            fontSize: "16px",
                                                            color: "text.secondary",
                                                            [catalogItemMediaStackMq]: {
                                                                fontSize: "14px",
                                                            },
                                                        }}
                                                    >
                                                        Описание пока не добавлено.
                                                    </Typography>
                                                ) : (
                                                    <RichTextHtml html={data.description}/>
                                                )}
                                            </Box>
                                            {showCatalogExpandControl ? (
                                                <Box sx={catalogTab1CharacteristicsFooterSx}>
                                                    <ButtonText
                                                        orange
                                                        onClick={() =>
                                                            setCatalogCardExpanded((expanded) => !expanded)
                                                        }
                                                        text={
                                                            catalogCardExpanded
                                                                ? "Свернуть"
                                                                : "Развернуть"
                                                        }
                                                        sx={catalogCompactButtonTextSx}
                                                    />
                                                </Box>
                                            ) : null}
                                        </Box>
                                    ) : null}
                                </Box>
                                <Box ref={catalogInfoActionsRef} sx={catalogInfoActionsSx}>
                                    {tab === 2 ? (
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={catalogResetTooltip}
                                        >
                                            <Box
                                                component="span"
                                                sx={{display: "inline-flex", maxWidth: "100%"}}
                                            >
                                                <ButtonCancel
                                                    text="Сброс"
                                                    onClick={requestCatalogReset}
                                                    disabled={isCatalogResetDisabled}
                                                    height={42}
                                                    sx={catalogInfoActionsCancelButtonSx}
                                                />
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={catalogWidthRecalculating
                                                ? CATALOG_WIDTH_RECALCULATING_BUTTON_TOOLTIP
                                                : ""}
                                        >
                                            <Box
                                                component="span"
                                                sx={{display: "inline-flex", maxWidth: "100%"}}
                                            >
                                                <ButtonAdd
                                                    text={"Оформить заказ"}
                                                    height={42}
                                                    sx={[
                                                        catalogCompactButtonAddSx,
                                                        catalogInfoActionsButtonSx,
                                                    ]}
                                                    loading={isCreatingOrder}
                                                    disabled={catalogWidthRecalculating}
                                                    onClick={handlePlaceOrderClick}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    {(showCurrentPriceBlock || showRecalculatePriceButton) && (
                                        showCurrentPriceBlock ? (
                                            <Tooltip
                                                arrow
                                                placement="top"
                                                title={CATALOG_CURRENT_PRICE_TOOLTIP}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "inline-flex",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                <Box sx={catalogItemPriceBlockSx}>
                                                    <Typography sx={catalogItemPriceLabelSx}>
                                                        Текущая цена:
                                                    </Typography>
                                                    <Typography sx={catalogItemPriceTextSx}>
                                                        {formatCatalogPriceRub(data.price, {
                                                            from: compositionDraftId == null,
                                                        })}
                                                    </Typography>
                                                </Box>
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip
                                                arrow
                                                placement="top"
                                                title={catalogWidthRecalculating
                                                    ? CATALOG_WIDTH_RECALCULATING_BUTTON_TOOLTIP
                                                    : ""}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{display: "inline-flex", maxWidth: "100%"}}
                                                >
                                                    <ButtonAdd
                                                        text={"Рассчитать цену"}
                                                        height={42}
                                                        width="auto"
                                                        minWidth="auto"
                                                        loading={priceRecalculating}
                                                        disabled={catalogWidthRecalculating || priceRecalculating}
                                                        onClick={handleRecalculatePrice}
                                                        sx={[
                                                            catalogCompactButtonAddSx,
                                                            catalogInfoActionsButtonSx,
                                                            !priceRecalculating && recalculatePriceButtonShimmerSx,
                                                        ]}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        )
                                    )}
                                </Box>
                            </Box>
                        </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Container>
            <Box sx={catalogFixedOrderBarSx}>
                <Tooltip
                    arrow
                    placement="top"
                    title={catalogWidthRecalculating
                        ? CATALOG_WIDTH_RECALCULATING_BUTTON_TOOLTIP
                        : ""}
                >
                    <Box
                        component="span"
                        sx={{display: "inline-flex", width: "100%", maxWidth: "100%"}}
                    >
                        <ButtonAdd
                            text={"Оформить заказ"}
                            width={293}
                            height={34}
                            sx={catalogFixedOrderButtonSx}
                            loading={isCreatingOrder}
                            disabled={catalogWidthRecalculating}
                            onClick={handlePlaceOrderClick}
                        />
                    </Box>
                </Tooltip>
            </Box>
            <ModalAlert
                data={alertData}
                onClose={handleCloseAlert}/>
            <Dialog
                open={Boolean(catalogWidthConfirm)}
                onClose={handleCatalogWidthConfirmCancel}
                onKeyDown={handleCatalogWidthConfirmDialogKeyDown}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Вы подтверждаете изменение?
                </DialogTitle>
                {catalogWidthConfirmContext ? (
                    <CompositionModuleChangeConfirmDialogContent
                        primaryRow={catalogWidthConfirmContext.row}
                        dependentRows={catalogWidthConfirmContext.dependents}
                        dimensionLabel={catalogWidthConfirmContext.dimensionLabel}
                        previousValue={catalogWidthConfirmContext.previousValue}
                        nextValue={catalogWidthConfirmContext.nextValue}
                    />
                ) : null}
                <DialogActions sx={{p: "1.25rem"}}>
                    <ButtonExit
                        text="Нет"
                        onClick={handleCatalogWidthConfirmCancel}
                    />
                    <ButtonAdd
                        text="Да"
                        onClick={handleCatalogWidthConfirmSubmit}
                    />
                </DialogActions>
            </Dialog>
            <Dialog
                open={catalogAuthDialogOpen}
                onClose={() => setCatalogAuthDialogOpen(false)}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Войдите в аккаунт
                </DialogTitle>
                <DialogContent sx={{pt: 0, pb: 2, px: "1.25rem"}}>
                    <Typography sx={{fontSize: "16px", lineHeight: 1.5, textAlign: "center"}}>
                        Чтобы оформить заказ, войдите в аккаунт или зарегистрируйтесь.
                    </Typography>
                </DialogContent>
                <DialogActions sx={catalogAuthDialogActionsSx}>
                    <ButtonExit
                        text="Отмена"
                        width="auto"
                        sx={catalogAuthDialogButtonExitSx}
                        onClick={() => setCatalogAuthDialogOpen(false)}
                    />
                    <ButtonAdd
                        text="Войти"
                        width="auto"
                        minWidth="auto"
                        height="auto"
                        minHeight="auto"
                        sx={catalogAuthDialogButtonAddSx}
                        onClick={() => {
                            setCatalogAuthDialogOpen(false);
                            redirectToAuth();
                        }}
                    />
                    <ButtonAdd
                        text="Зарегистрироваться"
                        width="auto"
                        minWidth="auto"
                        height="auto"
                        minHeight="auto"
                        sx={catalogAuthDialogButtonAddSx}
                        onClick={() => {
                            setCatalogAuthDialogOpen(false);
                            redirectToRegistration();
                        }}
                    />
                </DialogActions>
            </Dialog>
            <Dialog
                open={catalogResetConfirmOpen}
                onClose={() => setCatalogResetConfirmOpen(false)}
                onKeyDown={handleCatalogResetConfirmDialogKeyDown}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Вы подтверждаете сброс?
                </DialogTitle>
                <DialogContent sx={{pt: 0, pb: 2, px: "1.25rem"}}>
                    <Typography sx={{fontSize: "16px", lineHeight: 1.5}}>
                        Будут восстановлены{" "}
                        <Box
                            component="span"
                            sx={{fontWeight: 600}}
                        >
                            размеры модулей по умолчанию
                        </Box>{" "}
                        и{" "}
                        <Box
                            component="span"
                            sx={{fontWeight: 600}}
                        >
                            исходная цена
                        </Box>
                        .
                    </Typography>
                </DialogContent>
                <DialogActions sx={{p: "1.25rem"}}>
                    <ButtonExit
                        text="Нет"
                        onClick={() => setCatalogResetConfirmOpen(false)}
                    />
                    <ButtonAdd
                        text="Да"
                        loading={catalogResetLoading}
                        onClick={performCatalogReset}
                    />
                </DialogActions>
            </Dialog>
            <Dialog
                open={orderConfirmOpen}
                onClose={handleCreateOrderCancel}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Создать заказ?
                </DialogTitle>
                <DialogContent sx={{pt: 0, pb: 2, px: "1.25rem"}}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: "12px"}}>
                        <Typography sx={{fontSize: "16px", lineHeight: 1.5}}>
                            <Box component="span" sx={{fontWeight: 600}}>
                                Шаблон:
                            </Box>{" "}
                            {data?.name ?? "—"}
                        </Typography>
                        <Typography sx={{fontSize: "16px", lineHeight: 1.5}}>
                            <Box component="span" sx={{fontWeight: 600}}>
                                Цена:
                            </Box>{" "}
                            {orderConfirmPriceText ?? "—"}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{p: "1.25rem"}}>
                    <ButtonExit
                        text="Отменить"
                        disabled={isCreatingOrder}
                        onClick={handleCreateOrderCancel}
                    />
                    <ButtonAdd
                        text="Создать заказ"
                        loading={isCreatingOrder}
                        onClick={handleCreateOrderConfirm}
                    />
                </DialogActions>
            </Dialog>
        </Paper>
}

export default CatalogItem